<template>
  <div
    class="no-overscroll mt-2 rounded-lg"
    :class="listOpen ? 'contain h-auto' : ''"
    style="-webkit-tap-highlight-color: transparent"
  >
    <div
      class="flex h-auto flex-col items-center justify-center w-full rounded-lg bg-white"
      :class="[listOpen ? '' : 'cursor-pointer']"
    >
      <header
        class="leading-relaxed flex items-center whitespace-nowrap w-full h-6 py-8 sm:py-8 px-6 sm:px-6 overflow-hidden rounded-lg select-none"
        :class="[
          listOpen
            ? 'bg-gray-600 hover:bg-gray-500 text-gray-100 hover:text-white'
            : 'bg-gray-800 hover:bg-gray-700 text-white hover:text-white'
        ]"
        @click.stop="toggleAccordion"
        style="-webkit-tap-highlight-color: transparent"
      >
        <div class="flex flex-row w-full relative float-left cursor-pointer">
          <div class="font-semibold leading-relaxed text-xl truncate">
            {{ name }}
          </div>

          <div class="flex-grow"></div>

          <transition name="fade-fast">
            <section class="flex items-center" v-if="listOpen" @click.stop>
              <search-box v-if="searchable" v-model="searchQuery" />

              <button
                v-if="button"
                class="flex items-center font-semibold cursor-pointer bg-transparent text-gray-400 hover:text-white"
                @click.stop="buttonClick(buttonData)"
              >
                <unicon
                  name="plus-circle"
                  class="w-6 h-6 justify-content z-30"
                />
              </button>
            </section>
          </transition>
          <unicon
            name="angle-down"
            class="flex-none ml-2 sm:ml-4 w-8 h-8 z-30 cursor-pointer"
            :class="[listOpen ? 'svg-flipped' : 'svg']"
          />
        </div>
      </header>

      <transition-expand>
        <div v-if="listOpen" class="w-full">
          <div
            v-if="sortOptions.length > 0"
            class="flex items-center px-5 pt-3 pb-1 text-sm font-medium select-none"
          >
            <button
              class="flex-grow text-left flex items-center transition-colors"
              :class="selectedSortIndex === -1 ? 'text-gray-600' : 'text-gray-400 hover:text-gray-600'"
              @click.stop="selectSortOption(-1)"
            >
              {{ primaryLabel }}<span v-if="selectedSortIndex === -1" class="ml-0.5">{{ sortAscending ? ' ↑' : ' ↓' }}</span>
            </button>
            <button
              v-for="(opt, i) in sortOptions"
              :key="i"
              class="ml-4 flex items-center transition-colors"
              :class="selectedSortIndex === i ? 'text-gray-600' : 'text-gray-400 hover:text-gray-600'"
              @click.stop="selectSortOption(i)"
            >
              {{ opt.label }}<span v-if="selectedSortIndex === i" class="ml-0.5">{{ sortAscending ? ' ↑' : ' ↓' }}</span>
            </button>
          </div>
          <slick-list
            class="w-full bg-transparent rounded-lg px-2 overflow-auto"
            style="max-height: 60vh"
          id="listz"
          lock-axis="y"
          :press-delay="1000"
          :value="sortedList"
          helper-class="text-white bg-blue-400 shadow-xl cursor-move"
          @sort-start="sortStart"
          @sort-move="sortMove"
          @sort-end="sortEnd"
          @input="handleInput($event)"
        >
          <slick-item
            class="flex items-center w-full bg-transparent rounded-lg select-none text-left font-bold"
            :class="[
              item.isSeparator
                ? 'cursor-default py-2'
                : isDraggable
                ? 'cursor-move p-2'
                : 'cursor-pointer p-3',
              !item.isSeparator &&
                (isDragging ? '' : 'hover:bg-gray-200 hover:text-gray-900'),
              index == 0 ? 'mt-2' : '',
              index == sortedList.length - 1 ? 'mb-2' : ''
            ]"
            v-for="(item, index) in sortedList"
            :index="index"
            :key="item.id"
            :item="item"
            :disabled="!isDraggable || item.isSeparator"
            :style="{ height: item.height + 'px' }"
            @click.native.capture="
              !item.isSeparator && buttonClick({ id: item.id })
            "
            @touchstart.native.capture="!item.isSeparator && onPress"
            @mousedown.native.capture="!item.isSeparator && onPress"
            @mouseup.native.capture="!item.isSeparator && onPressup"
            @blur.native.capture="!item.isSeparator && onPressup"
          >
            <template v-if="!item.isSeparator">
              <span
                v-if="isDraggable && showIndex"
                class="mr-2 -mb-px text-sm px-2 bg-gray-400 font-semibold text-gray-900 rounded-full"
                style="min-width: 1.5em"
              >
                {{ indexCallback(item, index) }}
              </span>
              <span
                class="ml-1 leading-none flex-grow bg-transparent"
                :class="item.active ? 'text-gray-800' : 'text-gray-300'"
              >
                {{ noCapitalize ? item.name : capitalize(item.name) }}
              </span>
              <span
                v-if="rightBadge || (sortOptions.length > 0 && item.secondaryValue)"
                class="ml-2 -mb-px text-sm px-2 font-semibold rounded-full text-right"
                :class="
                  indexColor == 'none'
                    ? `text-gray-600`
                    : `bg-${indexColor}-400 text-${indexColor}-900`
                "
                style="min-width: 1.5em"
              >
                {{ sortOptions.length > 0 ? item.secondaryValue : rightBadgeCallback(item, index) }}
              </span>
              <span v-if="item.iconProperty & icons.length">
                <div
                  class="ml-2 -mt-px text-sm font-semibold text-gray-500"
                  style="min-width: 1.5em"
                >
                  <unicon :name="icons[0]" class="w-5 h-5" />
                </div>
              </span>
            </template>
            <template v-else>
              <div class="w-full flex items-center">
                <div class="flex-grow border-t border-gray-300"></div>
                <span
                  class="mx-4 text-sm text-gray-500 font-medium uppercase"
                  >{{ item.name }}</span
                >
                <div class="flex-grow border-t border-gray-300"></div>
              </div>
            </template>
          </slick-item>
        </slick-list>
      </div>
      </transition-expand>
    </div>
  </div>
</template>

<script>
import TransitionExpand from '@/components/TransitionExpand'
import { SlickList, SlickItem } from 'vue-slicksort'
import { setTimeout } from 'timers'
import SearchBox from '@/components/SearchBox'
import { capitalize } from '@/utils.js'

export default {
  name: 'ButtonList',
  props: {
    name: {
      type: String,
      required: false,
      default: ''
    },
    button: {
      type: String,
      required: false,
      default: ''
    },
    buttonData: {
      type: Object,
      required: false,
      default: null
    },
    list: {
      type: [Object, Array],
      required: true
    },
    icons: {
      type: Array,
      required: false,
      default: () => []
    },
    iconProperty: {
      type: String,
      required: false,
      default: ''
    },
    display: {
      type: [String, Object],
      required: false,
      default: null
    },
    displayProperty: {
      type: String,
      required: false,
      default: null
    },
    displayCallback: {
      type: Function,
      required: false,
      default: null
    },
    indexCallback: {
      type: Function,
      required: false,
      default: (item, index) => index + 1
    },
    showIndex: {
      type: Boolean,
      required: false,
      default: true
    },
    indexColor: {
      type: String,
      required: false,
      default: 'gray'
    },
    rightBadge: {
      type: Boolean,
      required: false,
      default: false
    },
    rightBadgeCallback: {
      type: Function,
      required: false,
      default: () => ''
    },
    isActiveProperty: {
      type: String,
      required: false,
      default: null
    },
    secondColumn: {
      type: String,
      required: false,
      default: null
    },
    id: {
      type: String,
      required: false,
      default: 'id'
    },
    open: {
      type: Boolean,
      required: false,
      default: false
    },
    searchable: {
      type: Boolean,
      required: false,
      default: true
    },
    sortAlpha: {
      type: Boolean,
      required: false,
      default: false
    },
    draggable: {
      type: Boolean,
      required: false,
      default: false
    },
    sortOptions: {
      type: Array,
      required: false,
      default: () => []
    },
    noCapitalize: {
      type: Boolean,
      required: false,
      default: false
    },
    primaryLabel: {
      type: String,
      required: false,
      default: 'Name'
    },
    defaultBadgeIndex: {
      type: Number,
      required: false,
      default: -1
    }
  },
  data() {
    return {
      selectedSortIndex: -1,
      sortAscending: true,
      activeBadgeIndex: this.defaultBadgeIndex,
      options: {
        direction: 'horizontal',
        accepts: function (el) {
          //, target) {
          return !el.classList.contains('no-dnd')
        },
        moves: function (el) {
          //, container, handle) {
          return !el.classList.contains('no-dnd')
        }
      },
      true: true,
      listArray: [],
      dragLock: false,
      sortLock: false,
      listOpen: Boolean,
      localList: [],
      searchQuery: '',
      isDragging: false,
      isDraggable: false,
      selectedIndex: null,
      observer: new MutationObserver((mutations) => {
        mutations.forEach(() => {
          if (
            this.browserHasVibrationSupport &&
            window.navigator &&
            window.navigator.vibrate
          ) {
            window.navigator.vibrate([1])
          }
        })
      }),
      foo: null
    }
  },
  watch: {
    list() {
      this.localList = this.list
    },
    searchQuery() {
      if (this.searchQuery.trim().length > 0) {
        // HAS SEARCH QUERY
        this.onPressup()
        this.isDraggable = false
      } else {
        // NO SEARCH QUERY
        this.isDraggable = this.draggable
        this.onPress()
      }
    }
  },
  computed: {
    browserHasVibrationSupport() {
      // if (
      //   window &&
      //   window.navigator &&
      //   typeof window.navigator.vibrate !== 'undefined'
      // )
      //   return true
      return false
      // return window && window.navigator && typeof window.navigator.vibrate !== 'undefined'
    },
    sortedList() {
      if (Array.isArray(this.list)) {
        let clone = new Array()
        this.localList.forEach((item) => {
          if (
            this.display &&
            Object.prototype.hasOwnProperty.call(this.display, item)
          )
            clone.push({
              id: item,
              name: this.display
                ? this.displayProperty
                  ? this.displayCallback
                    ? this.displayCallback(item)
                    : this.display[item][this.displayProperty]
                  : this.display[item]
                : item,
              active:
                this.display &&
                this.isActiveProperty &&
                Object.prototype.hasOwnProperty.call(
                  this.display[item],
                  this.isActiveProperty
                )
                  ? this.display[item][this.isActiveProperty]
                  : true,
              iconProperty:
                this.display &&
                this.iconProperty &&
                Object.prototype.hasOwnProperty.call(
                  this.display[item],
                  this.iconProperty
                )
                  ? this.display[item][this.iconProperty]
                  : false,
              isSeparator:
                this.display &&
                Object.prototype.hasOwnProperty.call(
                  this.display[item],
                  'isSeparator'
                )
                  ? this.display[item].isSeparator
                  : false
            })
          else
            clone.push({
              id: item,
              name: item,
              active: true,
              isSeparator: false
            })
        })

        return clone.filter((id) => {
          if (this.searchQuery.trim().length > 0) {
            return (
              id.name
                .toLowerCase()
                .indexOf(this.searchQuery.toLowerCase().trim()) >= 0
            )
          }
          return true
        })
      } else {
        let clone = new Array()
        let keys = Object.keys(this.list)

        let index = 1
        keys.forEach((item) => {
          clone.push({
            id: (this.list[item].id || item).toString(),
            name: (this.displayCallback
              ? this.displayCallback(item)
              : this.list[item].name || item
            ).toString(),
            position:
              this.list[item].position || this.list[item].weekday || index,
            active:
              this.display &&
              this.isActiveProperty &&
              Object.prototype.hasOwnProperty.call(
                this.display[item],
                this.isActiveProperty
              )
                ? this.display[item][this.isActiveProperty]
                : true,
            iconProperty:
              this.display &&
              this.iconProperty &&
              Object.prototype.hasOwnProperty.call(
                this.display[item],
                this.iconProperty
              )
                ? this.display[item][this.iconProperty]
                : false,
            secondaryValue:
              this.sortOptions.length > 0 && this.activeBadgeIndex >= 0
                ? (this.sortOptions[this.activeBadgeIndex].getValue(this.list[item]) || '').toString()
                : ''
          })
          index++
        })
        return clone
          .filter((id) => {
            if (this.searchQuery.trim().length > 0) {
              return (
                id.name
                  .toLowerCase()
                  .indexOf(this.searchQuery.toLowerCase().trim()) >= 0
              )
            }
            return true
          })
          .sort((a, b) => {
            if (this.sortOptions.length > 0) {
              let aVal, bVal
              if (this.selectedSortIndex >= 0) {
                aVal = (a.secondaryValue || '').toLowerCase()
                bVal = (b.secondaryValue || '').toLowerCase()
                if (aVal === bVal) {
                  aVal = a.name.toLowerCase()
                  bVal = b.name.toLowerCase()
                }
              } else {
                aVal = a.name.toLowerCase()
                bVal = b.name.toLowerCase()
              }
              const cmp = aVal.localeCompare(bVal)
              return this.sortAscending ? cmp : -cmp
            }
            if (this.sortAlpha)
              return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            else return a.position > b.position ? 1 : -1
          })
      }
    }
  },
  components: {
    SlickList,
    SlickItem,
    TransitionExpand,
    SearchBox
  },
  created() {
    this.listOpen = this.open
    this.isDraggable = this.draggable
  },
  mounted() {
    this.localList = this.list
  },
  methods: {
    sortStart() {
      this.isDragging = true
    },
    sortEnd() {
      this.isDragging = false
      this.observer.disconnect()
      this.foo = null
    },
    handleInput(payload) {
      this.localList = payload.map((item) => item.id)
      this.$emit('update', this.localList) // payload
    },
    sortMove() {
      if (this.foo === null) {
        this.foo = document.getElementById('listz')
        this.observer.observe(this.foo, {
          attributes: true,
          subtree: true,
          attributeFilter: ['style']
        })
      }
    },
    onPress() {
      this.$store.dispatch('config/setDragLock', true)
      setTimeout(() => {
        this.$store.dispatch('config/setDragLock', false)
      }, 1000)
    },
    onPressup() {
      this.$store.dispatch('config/setDragLock', false)
    },
    touch() {
      this.$store.dispatch('config/setDragLock', true)
    },
    capitalize,
    selectSortOption(index) {
      if (this.selectedSortIndex === index) {
        this.sortAscending = !this.sortAscending
      } else {
        this.selectedSortIndex = index
        this.sortAscending = true
      }
      if (index >= 0) {
        this.activeBadgeIndex = index
      }
    },
    buttonClick(payload) {
      this.$emit('edit', payload)
    },
    toggleAccordion: function () {
      this.$nextTick(() => {
        this.listOpen = !this.listOpen
        this.searchQuery = ''
      })
    }
  }
}
</script>

<style scoped>
.svg {
  transition: transform 0.3s 0.05s ease-out;
  transform-origin: 50% 50%;
  transform: rotate(0);
}
.svg-flipped {
  transition: transform 0.25s 0s ease-out;
  transform-origin: 50% 50%;
  transform: rotate(-180deg);
}
.cursor-move {
  cursor: row-resize;
}
::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 7px;
}
::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.5);
  box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
}
.expand-enter,
.expand-leave-to {
  height: 0;
  opacity: 0;
}
.expand-enter-active {
  overflow: hidden;
  transition: height 0.45s 0s cubic-bezier(0.76, 0, 0.24, 1),
    opacity 0.2s 0.2s ease-out;
  /* //if multiple expands, use this one */
  /* //transition: height 0.25s 0.3s ease-out, opacity 0.3s 0.5s ease-out; */
}
.expand-leave-active {
  overflow: hidden;
  /* //if multiple expands, use this one */
  transition: opacity 0.05s ease-out,
    height 0.4s 0s cubic-bezier(0.76, 0, 0.24, 1);
}
* {
  will-change: height, opacity;
  transform: translateZ(0);
}
</style>
